/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Breadcrumbs, Heading } from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { v4 as uuid } from 'uuid';

import TripPlannerHero from '../components/TripPlannerHero';
import TripFiltersSteps from '../components/TripFiltersSteps';
import TripDestinationsForm from '../components/TripDestinationsForm';
import TripFiltersForm from '../components/TripFiltersForm';
import TripPlannerSidemenu from '../components/TripPlannerSidemenu';
import { isTripValid } from '../utils/tripUtils';
import { cloneState } from '../utils/stateUtils';
import * as tripPlannerActions from '../actions/tripPlanner';
import * as types from '../constants';

const breadCrumbPath = [{ title: 'Home', url: '/' }, { title: 'Trip Builder' }];

export class TripPlannerStartContainer extends React.Component {
    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        tripPlanner: PropTypes.object,
        dispatch: PropTypes.func,
    };

    constructor(props) {
        super();
        const trip = JSON.parse(JSON.stringify(props.tripPlanner.trip));

        this.state = {
            showErrors: false,
            errorMessage: null,
            trip: {
                ...trip,
                destinations: this.getDestinationsWithID(trip.destinations),
            },
        };
        this.onStartPressed = this.onStartPressed.bind(this);
        this.onTripIdeaSelected = this.onTripIdeaSelected.bind(this);
        this.onClearItinerary = this.onClearItinerary.bind(this);
        this.onTripChange = this.onTripChange.bind(this);
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.refresh();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            JSON.stringify(this.state.trip) !== JSON.stringify(nextProps.tripPlanner.trip)
        ) {
            this.setState({
                trip: {
                    ...nextProps.tripPlanner.trip,
                    destinations: this.getDestinationsWithID(
                        nextProps.tripPlanner.trip.destinations
                    ),
                },
            });
        }
    }

    getDestinationsWithID(destinations = []) {
        let nextDestinations = [];

        nextDestinations = destinations.map((destination) => ({
            ...destination,
            /**
             * Each time a suggestion is picked, this component
             * re-renders.
             * During re-render, we are mapping over destinations
             * in ui-trip-planner/src/components/TripDestinationsForm.jsx
             * Generating a new ID on every render could lead to
             * unexpected behavior, such as loss of state.
             * To avoid this, we ensure that each destination
             * retains its existing ID, if present, or generate
             * a new one only if the ID is not already set.
             */
            id: destination.id ? destination.id : uuid(),
        }));

        return nextDestinations;
    }

    onTripIdeaSelected(t) {
        this.setState({
            showErrors: false,
            errorMessage: null,
        });

        const trip = JSON.parse(JSON.stringify(t));

        this.props.dispatch(tripPlannerActions.setActiveDate(null));
        this.props.dispatch(tripPlannerActions.setActiveDestinationIndex(null));
        this.props.dispatch(tripPlannerActions.setTrip(trip));
        this.props.dispatch(tripPlannerActions.setView('map'));
        this.props.dispatch(tripPlannerActions.resetResults());

        this.props.router.navigate(`/trips/${trip.trip_id}`);

        this.props.dispatch(tripPlannerActions.fetchDrivingDirections(trip));
        this.props.dispatch(tripPlannerActions.fetchDetailedRoute(trip));

        setTimeout(() => {
            this.props.dispatch(tripPlannerActions.setEnableOverviewModal(true));
        }, 500);
    }

    onStartPressed() {
        const trip = this.state.trip;
        const valid = isTripValid(trip);

        if (!valid) {
            this.setState({
                showErrors: true,
            });
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, 50);
        } else {
            this.setState({
                showErrors: false,
                errorMessage: null,
            });

            trip.trip_id = null;
            trip.account_id = null;
            let tripName = trip.name;

            if (!tripName || tripName === '') {
                const destinations = this.state.trip.destinations;
                const name = destinations[destinations.length - 1].name;
                const parts = name.split(',');
                tripName = `My Trip to ${parts[0]}`;
                trip.name = tripName;
            }

            this.props.dispatch(tripPlannerActions.setActiveDate(null));
            this.props.dispatch(tripPlannerActions.setActiveDestinationIndex(null));
            this.props.dispatch(tripPlannerActions.setTrip(trip));
            this.props.dispatch(tripPlannerActions.setView('map'));
            this.props.dispatch(tripPlannerActions.resetResults());

            this.props
                .dispatch(tripPlannerActions.saveTrip(this.props.router.navigate))
                .catch(() => {
                    this.setState({
                        showErrors: true,
                        errorMessage: true,
                    });
                });

            this.props.dispatch(tripPlannerActions.fetchDrivingDirections(trip));
            this.props.dispatch(tripPlannerActions.fetchDetailedRoute(trip));
        }
    }

    onClearItinerary() {
        this.props.dispatch(
            tripPlannerActions.setTrip(cloneState(types.BLANK_TRIP_TEMPLATE))
        );
    }

    onTripChange(trip) {
        this.setState(
            {
                trip,
            },
            () => {
                this.props.dispatch(tripPlannerActions.setTrip(trip));
            }
        );
    }

    refresh() {
        // This container only renders inside <RequireAuthExternal>, so the user
        // is guaranteed to be a logged-in registered user by the time we get here.
        this.props.dispatch(tripPlannerActions.fetchTripIdeas());
        this.props.dispatch(tripPlannerActions.fetchUserTrips());
        this.props.dispatch(tripPlannerActions.fetchUserPreferences());
    }

    render() {
        return (
            <div className="trip-planner-landing">
                <TripPlannerHero />

                <div id="page-body" className="trip-planner-landing-wrap">
                    <div className="rec-nested-wrap">
                        <div className="rec-breadcrumb-wrap">
                            <Breadcrumbs path={breadCrumbPath} />
                        </div>

                        <div className="trip-planner-body-wrap">
                            <section className="trip-planner-top-wrap">
                                <Heading headingLevel={2} className="rec-sr-only">
                                    Plan a Trip
                                </Heading>
                                <p>
                                    A good starting point is picking a time and place.
                                    From there, you can add on the layers - your ideal
                                    campsite, tours and guided activities, hiking,
                                    wildlife viewing, rafting excursions and so much more
                                    - and enhance your {process.env.SITE_NAME} experience
                                    and opportunity to bring home a story!
                                </p>
                            </section>

                            <TripFiltersSteps />

                            <TripDestinationsForm
                                trip={this.state.trip}
                                showClearButton
                                onChange={this.onTripChange}
                                onClear={this.onClearItinerary}
                                errorMessage={this.state.errorMessage}
                                showErrors={this.state.showErrors}
                            />

                            <TripFiltersForm />

                            <Button onClick={this.onStartPressed}>Start Planning</Button>
                        </div>

                        <TripPlannerSidemenu
                            tripIdeas={this.props.tripPlanner.tripIdeas}
                            userTrips={this.props.tripPlanner.trips}
                            onTripIdeaSelected={this.onTripIdeaSelected}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

function select(state) {
    return {
        search: state.search,
        tripPlanner: state.tripPlanner,
    };
}

export default withCompatRouter(connect(select)(TripPlannerStartContainer));
